/**
 * useMembershipPlanTranslations
 * 
 * Fetches translated plan names and descriptions from localization_metadata
 * for the current primary locale. Skips the query for 'en' locale since
 * the source data in users_membership_plans is already English.
 */

import { useQuery } from '@tanstack/react-query';
import { db } from '@/lib/supabaseQuery';
import { usePrimaryLocale } from '@/hooks/usePrimaryLocale';
import { useCurrentLocale } from '@/hooks/useCurrentLocale';

export interface PlanTranslation {
  translatedName: string | null;
  translatedBody: string[] | null;
}

function parseTranslatedBody(raw: string | string[] | null): string[] | null {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function useMembershipPlanTranslations() {
  const regionalLocale = useCurrentLocale();
  const { resolveToPrimary, isLoading: localeLoading } = usePrimaryLocale();
  const primaryLocale = resolveToPrimary(regionalLocale);
  const isEnglish = primaryLocale === 'en';

  const { data: translationsMap } = useQuery({
    queryKey: ['membership-plan-translations', primaryLocale],
    enabled: !isEnglish && !localeLoading,
    queryFn: async () => {
      const rows = await db.fetchAll(
        'localization_metadata',
        'source_id, translated_name, translated_body',
        {
          filters: [
            { column: 'source_type', operator: 'eq', value: 'membership_plan' },
            { column: 'locale', operator: 'eq', value: primaryLocale },
          ],
        }
      );

      const map = new Map<string, PlanTranslation>();
      for (const row of rows) {
        const r = row as Record<string, unknown>;
        map.set(String(r.source_id), {
          translatedName: (r.translated_name as string) || null,
          translatedBody: parseTranslatedBody(r.translated_body as string | string[] | null),
        });
      }
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    getTranslation: (sourceId: string): PlanTranslation | undefined =>
      translationsMap?.get(sourceId),
    isEnglish,
  };
}
